import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWatches, useModuleCatalog, moduleMaturity, selectWatchedRecentChanges } from '../../common-showcase';

/**
 * WatchedChangesCard — queue #677 §UAT-2026-07-13-R677 §R677-4③ · `common-showcase.md` §R677-cross.
 *
 * 대시보드 '내 구독 모듈 최근 변경' 카드 — common-showcase 가 public re-export 하는
 * `useWatches()`(구독 코드) × `useModuleCatalog()`(code→artifact) × `moduleMaturity`
 * (§R643 산출물)를 `selectWatchedRecentChanges` 순수 셀렉터로 조합해 최신순 5건을 렌더한다.
 * 신규 집계 endpoint 0(§R672 동일 커밋 메타 재사용) · 딥링크는 §R679 해시(`/showcase#<code>`)
 * 재사용. 레이아웃은 `PopularModulesCard`(§R662) 카드 관례 답습.
 *
 * 5상태: default(5건) · loading(스켈레톤 3행) · empty(구독0 안내 + `/showcase` 링크) ·
 * error/미인증(카드 통째 생략 — fail-soft, `GET /watches` 는 catch-all `.authenticated()`
 * 라 미인증은 401→isError 로 자연 커버) · dialog(n/a, 읽기 전용 카드).
 */
export function WatchedChangesCard() {
  const { t } = useTranslation();
  const { data: watches, isLoading: watchesLoading, isError: watchesError } = useWatches();
  const { data: catalog, isLoading: catalogLoading, isError: catalogError } = useModuleCatalog();

  // error/미인증(401) — 부가 위젯이라 대시보드 본체 무중단(fail-soft · §R662 계보)
  if (watchesError || catalogError) return null;

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface-muted)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-4)',
    marginTop: 'var(--spacing-4)',
  };
  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--spacing-3)',
  };
  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 'var(--font-size-base)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  };
  const viewAllLinkStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-brand-primary)',
    textDecoration: 'none',
    fontWeight: 600,
  };
  const emptyTextStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  };

  const isLoading = watchesLoading || catalogLoading || !watches || !catalog;

  if (isLoading) {
    return (
      <div aria-label={t('_index.watchedChanges.title')} style={cardStyle}>
        <div style={headerRowStyle}>
          <h2 style={titleStyle}>{t('_index.watchedChanges.title')}</h2>
        </div>
        <div
          role="status"
          aria-label={t('_index.watchedChanges.title')}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 32,
                background: 'var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                animation: 'pulse 1.5s ease-in-out infinite',
                opacity: 1 - i * 0.15,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const watchedCodes = watches.moduleCodes;

  // 구독0 — 위젯 자체는 노출하되 안내 + 카탈로그 링크(queue #677 §R677-4③ empty 상태)
  if (watchedCodes.length === 0) {
    return (
      <div aria-label={t('_index.watchedChanges.title')} style={cardStyle}>
        <div style={headerRowStyle}>
          <h2 style={titleStyle}>{t('_index.watchedChanges.title')}</h2>
          <Link to="/showcase" style={viewAllLinkStyle}>
            {t('_index.watchedChanges.viewAll')}
          </Link>
        </div>
        <p style={emptyTextStyle}>{t('_index.watchedChanges.empty')}</p>
      </div>
    );
  }

  const changes = selectWatchedRecentChanges(moduleMaturity, catalog.nodes, watchedCodes, 5);

  return (
    <div aria-label={t('_index.watchedChanges.title')} style={cardStyle}>
      <div style={headerRowStyle}>
        <h2 style={titleStyle}>{t('_index.watchedChanges.title')}</h2>
        <Link to="/showcase" style={viewAllLinkStyle}>
          {t('_index.watchedChanges.viewAll')}
        </Link>
      </div>

      {/* 구독은 있으나 commits 0건(전체 매핑 실패/미가용) — queue #677 §R677-4③ 변경0 상태 */}
      {changes.length === 0 ? (
        <p style={emptyTextStyle}>{t('_index.watchedChanges.noChanges')}</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          {changes.map((c, idx) => (
            <li key={`${c.code}-${c.date}-${idx}`}>
              {/* #336 R12 hash 오토셀렉트 계보 재사용(§R679 딥링크) — /showcase#<code> */}
              <Link
                to={`/showcase#${c.code}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-surface-border)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-primary)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <span style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)', flexShrink: 0 }}>
                  {c.code}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.subject}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                  {c.date}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
